// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { GuidedTourService } from '@med/app/home/demo/guided-tour/services/guided-tour.service';
import { Router } from '@angular/router';
import { Step } from '@med/app/home/demo/guided-tour/models/step';
import { plannerFullDemoSteps } from '@med/app/home/planner-full-demo/planner-full-demo-steps';
import { plannerFullDemoStepOrder } from '@med/app/home/planner-full-demo/planner-full-demo-step-order';
import { PlannerFullDemoStepEnum } from '@med/app/home/planner-full-demo/planner-full-demo-step.enum';

@Injectable({
  providedIn: 'root',
})
export class PlannerFullDemoService {

  private demoEnabled: boolean;
  private step: Step;
  private styleElements = new Map<string, HTMLStyleElement>();

  constructor(
    private guidedTourService: GuidedTourService,
    private router: Router,
  ) {
  }

  startDemo(): void {
    this.observePortalClicks();
    this.observeNextStepClicks();
    this.observeExitClicks();
    this.demoEnabled = true;
    const firstStepId = plannerFullDemoStepOrder.slice().shift();
    const firstStep = this.getStepById(firstStepId);
    this.executeStep(firstStep);
  }

  stopDemo(): void {
    this.demoEnabled = false;
  }

  getStepId(): PlannerFullDemoStepEnum {
    return this.step.stepId as PlannerFullDemoStepEnum;
  }

  isDemoEnabled(): boolean {
    return this.demoEnabled;
  }

  private observePortalClicks(): void {
    this.guidedTourService.portalClicked$.subscribe(() => {
      this.executeNextStep();
    });
  }

  private observeNextStepClicks(): void {
    this.guidedTourService.nextClicked$.subscribe(() => {
      this.executeNextStep();
    });
  }

  private observeExitClicks(): void {
    this.guidedTourService.exitDemoClicked$.subscribe(() => {
      document.location.href = '/campaigns';
    });
  }

  private executeNextStep(): void {
    const currentStepIndex = plannerFullDemoStepOrder.findIndex(id => this.step.stepId === id);
    const nextStepId = plannerFullDemoStepOrder[currentStepIndex + 1];
    const nextStep = this.getStepById(nextStepId);
    this.executeStep(nextStep);
  }

  private getStepById(id: string): Step {
    return plannerFullDemoSteps.find(s => s.stepId === id);
  }

  private executeStep(step: Step): void {
    this.step = step;
    step.dialog.stepCount = this.getStepCount();
    step.dialog.stepNumber = this.getStepNumber(step.stepId);
    this.guidedTourService.show(step);
    this.router.navigateByUrl(step.url).then(() => this.updateView(step));
  }

  private getStepNumber(stepId: string): number {
    return plannerFullDemoStepOrder.findIndex(id => id === stepId) + 1;
  }

  private getStepCount(): number {
    return plannerFullDemoStepOrder.length;
  }

  private disableStickyHeader(): void {
    const key = 'stickyHeaderDisabled';
    const rules = [
      'e-planner-header .topbar-container { position: static!important; }',
    ];
    const element = this.createStyles(rules);
    this.styleElements.set(key, element);
  }

  private createStyles(rules: string[]): HTMLStyleElement {
    const element = document.createElement('style') as HTMLStyleElement;
    document.head.appendChild(element);
    rules.forEach(rule => element.sheet.insertRule(rule));

    return element;
  }

  private toggleDisplayFooter(isVisible: boolean): void {
    const visibleFooterKey = 'visibleFooter';
    const hiddenFooterKey = 'hiddenFooter';

    if (isVisible) {
      if (!this.styleElements.has(visibleFooterKey)) {
        const rules = [
          'html { overflow: hidden!important; }',
        ];
        const element = this.createStyles(rules);
        this.styleElements.set(visibleFooterKey, element);
      }

      if (this.styleElements.has(hiddenFooterKey)) {
        this.styleElements.get(hiddenFooterKey).remove();
        this.styleElements.delete(hiddenFooterKey);
      }
    } else {
      if (!this.styleElements.has(hiddenFooterKey)) {
        const rules = [
          '.planner-footer { display: none!important; }',
          '.action-bar { display: none!important; }',
        ];
        const element = this.createStyles(rules);
        this.styleElements.set(hiddenFooterKey, element);
      }

      if (this.styleElements.has(visibleFooterKey)) {
        this.styleElements.get(visibleFooterKey).remove();
        this.styleElements.delete(visibleFooterKey);
      }
    }
  }

  private updateView(step: Step): void {
    switch (step.stepId) {
      case PlannerFullDemoStepEnum.plannerStartSelectAudience:
        this.disableStickyHeader();
        this.toggleDisplayFooter(false);
        break;
      case PlannerFullDemoStepEnum.demographicsAskToClickContinue:
        this.toggleDisplayFooter(true);
        break;
      case PlannerFullDemoStepEnum.mediaAskToClickContinue:
        this.toggleDisplayFooter(true);
        break;
      case PlannerFullDemoStepEnum.budgetAskToClickContinue:
        this.toggleDisplayFooter(true);
        break;
      case PlannerFullDemoStepEnum.audienceAskToClickContinue:
        this.toggleDisplayFooter(true);
        break;
      case PlannerFullDemoStepEnum.demographicsSelectGender:
        this.toggleDisplayFooter(false);
        break;
      case PlannerFullDemoStepEnum.mediaSearchPlatform:
        this.disableStickyHeader();
        this.toggleDisplayFooter(false);
        break;
      case PlannerFullDemoStepEnum.budgetSelectCampaignDuration:
        this.toggleDisplayFooter(false);
        break;
      case PlannerFullDemoStepEnum.spotplanAnalyze:
        this.toggleDisplayFooter(false);
        break;
      case PlannerFullDemoStepEnum.spotplanAskToClickSummary:
        this.toggleDisplayFooter(true);
        break;
      case PlannerFullDemoStepEnum.summaryAskToClickSendForReview:
        this.toggleDisplayFooter(true);
        break;
    }
  }
}
